// src/components/ThreeJsViewer.tsx
/**
 * Core 3D rendering component for 'Suntap' that provides the
 * foundation for visualising pub locations and gardens. Handles loading and
 * displaying 3D meshes, sets up Three.js scene, camera, and controls.
 * Implements view modes (plan view, 3D view), manages user interactions,
 * and provides methods for navigating the 3D space with focal points.
 */
import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Box, CircularProgress, Typography, Tooltip, IconButton, Button, Fade } from '@mui/material';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import ThreeSixtyIcon from '@mui/icons-material/ThreeSixty';
import MapIcon from '@mui/icons-material/Map';
import { convertToInternalCoords, createTextSprite, updateLineMaterialsResolution, disposeObject3D } from '../../utils/threeUtils';

export interface FocalPoint {
  name: string;
  coordinates: [number, number, number];
}

export interface ThreeJsViewerProps {
  meshUris: string[];
  initialZoom?: number;
  focalPoint?: [number, number, number]; // Initial center point [x, y, z] API coords
  focalPoints?: FocalPoint[]; // Optional list of named focal points
  skipRaycasting?: boolean; // Skip raycasting for initial target (useful for single garden view)
  pubName?: string; // Name to display at the focal point
  height?: string;
  onMeshesLoaded?: (models: THREE.Object3D[], scene: THREE.Scene) => void;
  onGardenSelect?: (index: number, name: string) => void; // Callback when a focal point is selected
  onCanvasClick?: (event: MouseEvent, intersection: THREE.Intersection | null, raycaster: THREE.Raycaster) => void; // Callback for clicks on the canvas
}

export interface ThreeJsViewerRef {
  scene: THREE.Scene | null;
  camera: THREE.PerspectiveCamera | null;
  controls: OrbitControls | null;
  renderer: THREE.WebGLRenderer | null;
  labelRenderer: CSS2DRenderer | null;
  trueUpVector: THREE.Vector3 | null;
  container: HTMLDivElement | null;
  models: THREE.Object3D[];
  raycaster: THREE.Raycaster;
  enterPlanView: () => void;
  exitPlanView: () => void;
  resetView: () => void;
  focusOnPoint: (point: THREE.Vector3) => void;
}

const ThreeJsViewer = forwardRef<ThreeJsViewerRef, ThreeJsViewerProps>(({
  meshUris,
  initialZoom = 45,
  focalPoint,
  focalPoints,
  skipRaycasting = false,
  pubName,
  height = '600px',
  onMeshesLoaded,
  onGardenSelect,
  onCanvasClick,
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const labelRendererRef = useRef<CSS2DRenderer | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const trueUpVectorRef = useRef<THREE.Vector3 | null>(null);
  const modelsRef = useRef<THREE.Object3D[]>([]);
  const raycasterRef = useRef<THREE.Raycaster>(new THREE.Raycaster());
  const animationFrameRef = useRef<number | null>(null);
  const initialTargetRef = useRef<THREE.Vector3 | null>(null); // Store initial camera target
  const initialCameraPosRef = useRef<THREE.Vector3 | null>(null); // Store initial camera position

  const [loading, setLoading] = useState<boolean>(true);
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [isPlanView, setIsPlanView] = useState<boolean>(false);
  const [showOrbitMessage, setShowOrbitMessage] = useState<boolean>(false);

  // --- Imperative API ---
  // No dependency array: Ensures the exposed ref always has the latest .current values
  useImperativeHandle(ref, () => ({
    scene: sceneRef.current,
    camera: cameraRef.current,
    controls: controlsRef.current,
    renderer: rendererRef.current,
    labelRenderer: labelRendererRef.current,
    trueUpVector: trueUpVectorRef.current,
    container: containerRef.current,
    models: modelsRef.current,
    raycaster: raycasterRef.current,
    enterPlanView: handlePlanView,
    exitPlanView: handleReset, // Resetting exits plan view
    resetView: handleReset,
    focusOnPoint: (point: THREE.Vector3) => {
        if (controlsRef.current) {
            controlsRef.current.target.copy(point);
            controlsRef.current.update();
        }
    }
  })); // NO DEPENDENCY ARRAY

  // --- View Control Handlers ---
  const handleReset = useCallback(() => {
    if (!controlsRef.current || !initialTargetRef.current || !cameraRef.current) return;
    setIsPlanView(false);
    
    // Show orbit message briefly
    setShowOrbitMessage(true);
    setTimeout(() => {
      setShowOrbitMessage(false);
    }, 2000); // Display for 2 seconds
    
    // Reset camera to standard constraints without changing position
    controlsRef.current.minPolarAngle = 0;
    controlsRef.current.maxPolarAngle = Math.PI;
    controlsRef.current.enableRotate = true;
    controlsRef.current.enablePan = true; // Ensure pan is enabled
    controlsRef.current.update();
  }, []);

  const handlePlanView = useCallback(() => {
    if (!cameraRef.current || !controlsRef.current || !trueUpVectorRef.current) return;

    const camera = cameraRef.current;
    const controls = controlsRef.current;
    const trueUp = trueUpVectorRef.current;

    const currentDistance = camera.position.distanceTo(controls.target);
    const upDirection = new THREE.Vector3().copy(trueUp).normalize();

    // Position camera directly above the target
    camera.position.copy(controls.target).addScaledVector(upDirection, currentDistance);
    camera.up.copy(trueUp); // Set the camera's up vector
    camera.lookAt(controls.target);

    // Lock polar angle to look straight down
    controls.minPolarAngle = 0;
    controls.maxPolarAngle = 0.0001; // Use a tiny epsilon instead of 0
    controls.enableRotate = false;
    controls.enablePan = true; // Keep panning enabled
    controls.enableZoom = true;
    controls.update();

    setIsPlanView(true);
  }, []);

  const handleFocalPointChange = useCallback((coordinates: [number, number, number], index: number, name: string) => {
    if (!controlsRef.current || !cameraRef.current) return;

    const targetPosition = convertToInternalCoords(coordinates);
    controlsRef.current.target.copy(targetPosition);
    controlsRef.current.update();

    if (onGardenSelect) {
      onGardenSelect(index, name);
    }
    // Exit plan view if active when changing focal point
    if (isPlanView) {
        handleReset();
    }
  }, [onGardenSelect, isPlanView, handleReset]); // onGardenSelect IS used here

  // --- Canvas Click Handler ---
  const handleInternalCanvasClick = useCallback((event: MouseEvent) => {
      if (!onCanvasClick || !containerRef.current || !cameraRef.current || !sceneRef.current || modelsRef.current.length === 0) {
          return;
      }

      const rect = containerRef.current.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      const raycaster = raycasterRef.current;
      raycaster.setFromCamera(new THREE.Vector2(x, y), cameraRef.current);

      // Intersect with the loaded models
      const intersects = raycaster.intersectObjects(modelsRef.current, true);

      // Pass the raw event, first intersection (if any), and the raycaster
      onCanvasClick(event, intersects.length > 0 ? intersects[0] : null, raycaster);

  }, [onCanvasClick]); // onCanvasClick IS used here


  // --- Scene Setup Effect ---
  useEffect(() => {
    // Guard against running if container isn't ready or no meshes specified
    if (!containerRef.current || meshUris.length === 0) {
        console.warn("ThreeJsViewer useEffect skipped: No container or meshUris.");
        setLoading(false); // Ensure loading state is cleared if we skip setup
        return;
    }

    let isMounted = true;
    const currentContainer = containerRef.current;

    // --- Basic Scene Setup ---
    const scene = new THREE.Scene();
    sceneRef.current = scene;
    scene.background = new THREE.Color(0xf0f0f0);
    scene.fog = new THREE.Fog(0xf0f0f0, 55, 80);

    const camera = new THREE.PerspectiveCamera(
      60,
      currentContainer.clientWidth / currentContainer.clientHeight,
      0.1,
      10000
    );
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    rendererRef.current = renderer;
    renderer.setSize(currentContainer.clientWidth, currentContainer.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    currentContainer.appendChild(renderer.domElement);

    const labelRenderer = new CSS2DRenderer();
    labelRendererRef.current = labelRenderer;
    labelRenderer.setSize(currentContainer.clientWidth, currentContainer.clientHeight);
    labelRenderer.domElement.style.position = 'absolute';
    labelRenderer.domElement.style.top = '0';
    labelRenderer.domElement.style.pointerEvents = 'none';
    currentContainer.appendChild(labelRenderer.domElement);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
    scene.add(ambientLight);
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(1, 1, 1).normalize();
    scene.add(directionalLight);

    // --- Loaders ---
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
    const gltfLoader = new GLTFLoader();
    gltfLoader.setDRACOLoader(dracoLoader);
    const manager = new THREE.LoadingManager();

    // --- Loading Manager Callbacks ---
    manager.onProgress = (url, itemsLoaded, itemsTotal) => {
      if (isMounted) setProgress((itemsLoaded / itemsTotal) * 100);
    };
    manager.onError = (url) => {
      console.error('Loading Manager Error:', url);
      if (isMounted) setError(`Failed to load resource via manager: ${url}`);
    };
    manager.onLoad = () => {
       if (isMounted) {
           setLoading(false);
           // Setup scene geometry, controls, camera etc. AFTER models are in scene
           setupSceneAfterLoading(modelsRef.current, camera, scene);
           // Trigger the external callback *after* internal setup is complete
           if (onMeshesLoaded) {
               onMeshesLoaded(modelsRef.current, scene);
           }
       }
    };
    gltfLoader.manager = manager;

    // --- State Reset and Model Loading ---
    setLoading(true);
    setError(null);
    setProgress(0);
    modelsRef.current = []; // Clear previous models

    meshUris.forEach((uri) => {
      gltfLoader.load(uri, (gltf) => {
        if (isMounted) {
          scene.add(gltf.scene);
          modelsRef.current.push(gltf.scene);
          // Wait for manager.onLoad to trigger setupSceneAfterLoading
        }
      }, undefined, (error) => {
          console.error(`Loader: Error loading model ${uri}:`, error);
          if (isMounted) setError(`Error loading ${uri}`);
          // Manager should handle overall load completion even if one file fails
      });
    });

    // --- Scene Geometry and Controls Setup (Called AFTER loading) ---
    const setupSceneAfterLoading = (
      models: THREE.Object3D[],
      camera: THREE.PerspectiveCamera,
      scene: THREE.Scene
    ) => {
        if (!isMounted || models.length === 0) {
            console.warn("setupSceneAfterLoading skipped: Component unmounted or no models.");
            if (isMounted) setError("No 3D models were loaded successfully.");
            setLoading(false);
            return;
        }

      const combinedBox = new THREE.Box3();
      models.forEach(model => {
          model.updateMatrixWorld(true);
          const modelBox = new THREE.Box3().setFromObject(model, true);
          if (!modelBox.isEmpty()) {
             combinedBox.union(modelBox);
          } else {
             console.warn("setupSceneAfterLoading: Empty bounding box for a model:", model.name);
          }
      });

       if (combinedBox.isEmpty()) {
           console.error("setupSceneAfterLoading: Combined bounding box is empty. Cannot determine center.");
           combinedBox.setFromCenterAndSize(new THREE.Vector3(0,0,0), new THREE.Vector3(1,1,1));
       }

      let targetPosition: THREE.Vector3;
      if (focalPoint) {
        targetPosition = convertToInternalCoords(focalPoint);
      } else {
        targetPosition = combinedBox.getCenter(new THREE.Vector3());
      }

      const calculatedTrueUp = new THREE.Vector3().copy(targetPosition).normalize();
      trueUpVectorRef.current = calculatedTrueUp;

      let surfaceTargetPosition = targetPosition.clone();
      if (!skipRaycasting) {
          scene.updateMatrixWorld(true);
          const raycaster = raycasterRef.current;
          const origin = new THREE.Vector3(0, 0, 0);
          const direction = targetPosition.clone().normalize();
          raycaster.set(origin, direction);
          raycaster.far = targetPosition.length() * 100;

          models.forEach(model => model.traverse(child => {
              if (child instanceof THREE.Mesh && child.material) {
                const materials = Array.isArray(child.material) ? child.material : [child.material];
                materials.forEach(m => m.side = THREE.DoubleSide);
              }
          }));

          const intersects = raycaster.intersectObjects(models, true);
          if (intersects.length > 0) {
              surfaceTargetPosition = intersects[0].point;
          } else {
              console.warn("setupSceneAfterLoading: Raycast did not intersect models. Using original target.");
          }

          // Go nuclear if we didn't succeed at first
          // This is a last resort to find a surface point - but is needed for some pubs
          if (!intersects.length) {
            console.log("Original raycast failed. Trying perturbed origins...");
            
            // Keep original direction, but try slightly perturbed origins
            const perturbations = [
                new THREE.Vector3(1, 0, 0),    // +X axis
                new THREE.Vector3(-1, 0, 0),   // -X axis
                new THREE.Vector3(0, 1, 0),    // +Y axis
                new THREE.Vector3(0, -1, 0),   // -Y axis
                new THREE.Vector3(0, 0, 1),    // +Z axis
                new THREE.Vector3(0, 0, -1),   // -Z axis
                new THREE.Vector3(1, 1, 1),    // Diagonal
                new THREE.Vector3(-1, -1, -1)  // Opposite diagonal
            ];
            
            let foundIntersection = false;
            
            // Try each perturbed origin while keeping the same direction
            for (const perturb of perturbations) {
                if (foundIntersection) break;
                
                // Scale perturbation to a reasonable size (2 meters in world space)
                const perturbedOrigin = perturb.clone().multiplyScalar(2);
                
                raycaster.set(perturbedOrigin, direction);
                const perturbedIntersects = raycaster.intersectObjects(models, true);
                
                if (perturbedIntersects.length > 0) {
                    console.log("Hit found with perturbed origin:", perturb);
                    surfaceTargetPosition = perturbedIntersects[0].point;
                    foundIntersection = true;
                }
            }
            
            // If still no intersection, try the fallback approach
            if (!foundIntersection) {
                console.log("All perturbed origins failed. Using fallback position.");
                surfaceTargetPosition = targetPosition.clone().addScaledVector(trueUpVectorRef.current, 30);
            }
        }

      } else {
          console.log("setupSceneAfterLoading: Skipping raycasting.");
      }
      initialTargetRef.current = surfaceTargetPosition.clone();
      

      if (pubName && trueUpVectorRef.current) {
        const pubNameSprite = createTextSprite(pubName);
        pubNameSprite.position.copy(surfaceTargetPosition).addScaledVector(trueUpVectorRef.current, 3);
        scene.add(pubNameSprite);
      }

      if (trueUpVectorRef.current) {
          camera.up.copy(trueUpVectorRef.current);
          const size = combinedBox.getSize(new THREE.Vector3());
          const maxDim = Math.max(size.x, size.y, size.z, 1.0);
          const fov = camera.fov * (Math.PI / 180);
          let cameraDistance = initialZoom || Math.max(maxDim / (2 * Math.tan(fov / 2)), 10);
          // Prevent excessively large distances if bounding box is huge relative to focal point
          cameraDistance = Math.min(cameraDistance, 1000); // Add a max cap if needed

          // Calculate camera position using azimuth, elevation and distance
          const calculateCameraPosition = (
            target: THREE.Vector3, 
            upVector: THREE.Vector3, 
            azimuth: number, 
            elevation: number, 
            distance: number
          ): THREE.Vector3 => {
            // Create a local coordinate system at the target point
            const upDir = new THREE.Vector3().copy(upVector).normalize();
            
            // Find a suitable right vector (perpendicular to up)
            const worldRight = new THREE.Vector3(1, 0, 0);
            // If up is too close to worldRight, use a different reference vector
            if (Math.abs(upDir.dot(worldRight)) > 0.9) {
              worldRight.set(0, 1, 0);
            }
            
            // Create orthogonal basis vectors
            const rightDir = new THREE.Vector3().crossVectors(worldRight, upDir).normalize();
            const forwardDir = new THREE.Vector3().crossVectors(upDir, rightDir).normalize();
            
            // Calculate the direction vector using spherical coordinates
            // elevation: 0 = horizon, PI/2 = directly above
            // azimuth: 0 = forward direction, measured clockwise
            const directionVector = new THREE.Vector3();
            const elevRad = elevation * Math.PI / 180;
            const azimuthRad = azimuth * Math.PI / 180;
            
            // Calculate components in the local coordinate system
            directionVector.x = Math.cos(elevRad) * Math.sin(azimuthRad); // Right component
            directionVector.y = Math.sin(elevRad);                         // Up component
            directionVector.z = Math.cos(elevRad) * Math.cos(azimuthRad);  // Forward component
            
            // Transform from local coordinates to world coordinates
            const worldDirection = new THREE.Vector3();
            worldDirection.addScaledVector(rightDir, directionVector.x);
            worldDirection.addScaledVector(upDir, directionVector.y);
            worldDirection.addScaledVector(forwardDir, directionVector.z);
            
            // Calculate the final position
            return new THREE.Vector3().copy(target).addScaledVector(worldDirection, distance);
          };
          
          const azimuth = 45;    // 45 degrees from forward direction
          const elevation = 60;  // 60 degrees up from horizon (steeper look down)
          camera.position.copy(calculateCameraPosition(
            surfaceTargetPosition, 
            trueUpVectorRef.current, 
            azimuth, 
            elevation, 
            cameraDistance
          ));
          camera.lookAt(surfaceTargetPosition);
          
          // Store initial camera position
          initialCameraPosRef.current = camera.position.clone();
      } else {
           console.error("setupSceneAfterLoading: Cannot position camera: True Up Vector missing.");
           camera.position.set(0, 10, 20);
           camera.lookAt(surfaceTargetPosition);
      }

      const controls = new OrbitControls(camera, renderer.domElement);
      controlsRef.current = controls;
      controls.target.copy(surfaceTargetPosition);
      controls.enableDamping = true;
      controls.dampingFactor = 0.1;
      controls.rotateSpeed = 0.5;
      controls.panSpeed = 0.8;
      controls.zoomSpeed = 1.0;
      controls.minDistance = 1;
      controls.maxDistance = (initialZoom || 100) * 5; // Use initialZoom in maxDistance calculation
      controls.minPolarAngle = 0;
      controls.maxPolarAngle = Math.PI * 0.95;

      controls.addEventListener('change', () => {
          if (isMounted && trueUpVectorRef.current && !isPlanView) {
             camera.up.copy(trueUpVectorRef.current);
          }
      });
      controls.update();
    };

    // --- Animation Loop ---
    const animate = () => {
      if (!isMounted) return;
      animationFrameRef.current = requestAnimationFrame(animate);
      if (rendererRef.current && sceneRef.current && cameraRef.current) {
        // Store camera reference in scene userData for raycasting
        if (sceneRef.current) {
          sceneRef.current.userData.camera = cameraRef.current;
        }
        
        controlsRef.current?.update();
        rendererRef.current.render(sceneRef.current, cameraRef.current);
        labelRendererRef.current?.render(sceneRef.current, cameraRef.current);
      }
    };
    animate();

    // --- Resize Handler ---
    const handleResize = () => {
        if (!isMounted || !currentContainer || !cameraRef.current || !rendererRef.current || !labelRendererRef.current) return;
        const width = currentContainer.clientWidth;
        const height = currentContainer.clientHeight;
        cameraRef.current.aspect = width / height;
        cameraRef.current.updateProjectionMatrix();
        rendererRef.current.setSize(width, height);
        labelRendererRef.current.setSize(width, height);
        if (sceneRef.current) {
             updateLineMaterialsResolution(sceneRef.current, width, height);
        }
    };
    window.addEventListener('resize', handleResize);

    // --- Click Listener ---
    if (onCanvasClick) {
        currentContainer.addEventListener('click', handleInternalCanvasClick);
    }

    // --- Cleanup ---
    return () => {
      isMounted = false;
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      window.removeEventListener('resize', handleResize);
      if (onCanvasClick && currentContainer) { // Add null check for currentContainer
          currentContainer.removeEventListener('click', handleInternalCanvasClick);
      }

      controlsRef.current?.dispose();

      // Safely dispose scene objects
      if (sceneRef.current) {
        sceneRef.current.traverse(object => {
            // Enhanced disposal
            if (object instanceof THREE.Mesh) {
                if (object.geometry) object.geometry.dispose();
                if (object.material) {
                    const materials = Array.isArray(object.material) ? object.material : [object.material];
                    materials.forEach(material => {
                        if (material.map) material.map.dispose();
                        // Dispose other textures (normalMap, etc.) if used
                        material.dispose();
                    });
                }
            } else if (object instanceof Line2) {
                // Properly dispose Line2 objects
                if (object.geometry) object.geometry.dispose();
                if (object.material) object.material.dispose();
            } else if (object instanceof THREE.Sprite) {
                if (object.material) {
                    if (object.material.map) object.material.map.dispose();
                    object.material.dispose();
                }
            }
            // Add other types as needed (lights, helpers, etc.)
        });
        // Remove all children after traversal/disposal
        while(sceneRef.current.children.length > 0){
            sceneRef.current.remove(sceneRef.current.children[0]);
        }
      }

      rendererRef.current?.dispose();
      if (labelRendererRef.current?.domElement) labelRendererRef.current.domElement.remove();
      if (rendererRef.current?.domElement) rendererRef.current.domElement.remove();
      dracoLoader.dispose();

      // Clear refs
      sceneRef.current = null;
      cameraRef.current = null;
      rendererRef.current = null;
      labelRendererRef.current = null;
      controlsRef.current = null;
      trueUpVectorRef.current = null;
      modelsRef.current = [];
      initialTargetRef.current = null;
      initialCameraPosRef.current = null;
    };
  // Dependencies: Key props affecting scene setup + callbacks used *within* the effect
  }, [meshUris, initialZoom, focalPoint?.toString(), skipRaycasting, pubName, onMeshesLoaded, onCanvasClick]); // Removed onGardenSelect


  return (
    <Box sx={{ position: 'relative', width: '100%', height }}>
      {/* View Controls */}
      <Box sx={{
        position: 'absolute',
        top: 8,
        right: 8,
        zIndex: 10, // Below drawing controls if they overlap
        display: 'flex',
        gap: 1
      }}>
        {focalPoints && focalPoints.length > 0 ? (
          focalPoints.map((point, index) => (
            <Tooltip key={index} title={`Focus on ${point.name}`}>
              <Button
                variant="contained"
                onClick={() => handleFocalPointChange(point.coordinates, index, point.name)}
                size="small"
                sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    color: '#FF8800', // Brand color
                    fontFamily: '"Darumadrop One", "Comic Sans MS", cursive, sans-serif', // Fancy font
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' },
                }}
              >
                {point.name}
              </Button>
            </Tooltip>
          ))
        ) : ( // Show default controls if no focal points list
          <>
            <Tooltip title="Reset view">
              <IconButton
                color="primary"
                onClick={handleReset}
                sx={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' } }}
                size="medium"
                disabled={loading || !!error}
              >
                <ThreeSixtyIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={isPlanView ? "3D view" : "Top-down view"}>
              <IconButton
                color={isPlanView ? "secondary" : "primary"}
                onClick={isPlanView ? handleReset : handlePlanView}
                sx={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' } }}
                size="medium"
                disabled={loading || !!error}
              >
                <MapIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      {/* Top-down view indicator */}
      {isPlanView && (
        <Box sx={{
          position: 'absolute',
          top: 8,
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(4px)',
          borderRadius: 1,
          padding: '2px 8px',
          zIndex: 10
        }}>
          <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
            Top-down view
          </Typography>
        </Box>
      )}

      {/* Orbit mode temporary message with fade-out */}
      <Fade in={showOrbitMessage && !isPlanView} timeout={{ enter: 300, exit: 800 }}>
        <Box sx={{
          position: 'absolute',
          top: 8,
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(4px)',
          borderRadius: 1,
          padding: '2px 8px',
          zIndex: 10,
          visibility: (showOrbitMessage && !isPlanView) ? 'visible' : 'hidden' // Ensures the box doesn't take space when faded out
        }}>
          <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
            Orbit mode
          </Typography>
        </Box>
      </Fade>

      {/* The div where the Three.js canvas will be appended */}
      <div
        ref={containerRef}
        style={{ width: '100%', height: '100%', position: 'relative', outline: 'none' }}
        // onClick listener is added dynamically in useEffect
      />

      {/* Loading Indicator */}
      {loading && (
        <Box
          sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)', display: 'flex',
            flexDirection: 'column', alignItems: 'center', gap: 2,
            color: 'text.secondary'
          }}
        >
          <CircularProgress />
          <Typography variant="body1">Loading 3D... {progress.toFixed(0)}%</Typography>
        </Box>
      )}

      {/* Error Indicator */}
      {error && (
        <Box
          sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)', color: 'error.main',
            backgroundColor: 'rgba(255, 255, 255, 0.8)', padding: 2, borderRadius: 1
          }}
        >
          <Typography variant="body1" align="center">Error loading model. <br /> {error}</Typography>
        </Box>
      )}

      {/* Google Attribution */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          left: 16,
          display: 'flex',
          alignItems: 'center',
          bgcolor: 'rgba(255, 255, 255, 0.8)',
          px: 1.5,
          py: 0.5,
          borderRadius: 1,
          backdropFilter: 'blur(4px)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          zIndex: 5
        }}
      >
        <Typography 
          variant="body2" 
          sx={{ 
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '14px',
            letterSpacing: '0.0575em',
            color: '#5F6368',
            marginRight: '8px'
          }}
        >
          ©{new Date().getFullYear()} Google
        </Typography>
      </Box>
    </Box>
  );
});

export default ThreeJsViewer;